'use client';

/**
 * Webhotell.tsx — produktside for webhotell (`/webhotell`)
 *
 * Følger ibm.com/products-sidene (virtual-servers, aix): brødsmulesti og hero
 * i grått bånd, fast seksjonsmeny, oversikt, funksjoner (akkordion), promo,
 * pakker, sammenligning, bruksområder, spørsmål og avsluttende CTA.
 * Header, footer, knapper, språkvalg og tema deles med forsiden. Ingen UI
 * Shell (ROUTES_WITHOUT_SHELL i ClientUiShell.tsx).
 *
 * Innhold: statisk, norsk + engelsk, se webhotell-content.ts (plassholdere!).
 */

import ClosingCta from '@/components/landing/ClosingCta';
import LandingFooter from '@/components/landing/LandingFooter';
import LandingHeader from '@/components/landing/LandingHeader';
import { MASTHEAD_CONTENT } from '@/components/landing/masthead-content';
import ComparisonTable from '@/components/product/ComparisonTable';
import FaqAccordion from '@/components/product/FaqAccordion';
import PricingCards from '@/components/product/PricingCards';
import FeatureAccordion from '@/components/product/FeatureAccordion';
import CubesIllustration from '@/components/webhotell/CubesIllustration';
import HeroIllustration from '@/components/webhotell/HeroIllustration';
import ProductHero from '@/components/product/ProductHero';
import PromoBanner from '@/components/product/PromoBanner';
import RelatedProducts from '@/components/product/RelatedProducts';
import SectionHeader from '@/components/product/SectionHeader';
import SectionNav from '@/components/product/SectionNav';
import UseCases from '@/components/product/UseCases';
import styles from '@/components/product/product.module.css';
import { useLocale } from '@/hooks/useLocale';
import landingStyles from '@/sections/landing/landing.module.css';
import { WEBHOTELL_CONTENT } from './webhotell-content';

export default function Webhotell() {
  const { locale, setLocale } = useLocale();
  const content = WEBHOTELL_CONTENT[locale];

  return (
    <div className={landingStyles.page}>
      <LandingHeader
        ui={content.ui}
        menu={MASTHEAD_CONTENT[locale]}
        locale={locale}
        onLocaleChange={setLocale}
      />
      <main id="innhold" className={landingStyles.main}>
        <ProductHero
          hero={content.hero}
          breadcrumb={content.breadcrumb}
          illustration={<HeroIllustration label={content.hero.illustrationLabel} />}
        />

        <SectionNav nav={content.sectionNav} />

        <section id="oversikt" className={styles.section} aria-labelledby="overview-heading">
          <div className={styles.container}>
            <SectionHeader
              id="overview-heading"
              eyebrow={content.overview.eyebrow}
              heading={content.overview.heading}
              blue
            >
              {content.overview.body.map((paragraph) => (
                <p key={paragraph} className={styles.lead}>
                  {paragraph}
                </p>
              ))}
            </SectionHeader>
          </div>
        </section>

        <FeatureAccordion
          features={content.features}
          art={<CubesIllustration label={content.features.panelLabel} />}
        />
        <PromoBanner promo={content.promo} />
        <PricingCards plans={content.plans} />
        <ComparisonTable comparison={content.comparison} plans={content.plans} />
        <UseCases useCases={content.useCases} />
        <RelatedProducts related={content.related} />
        <FaqAccordion faq={content.faq} />
        <ClosingCta cta={content.cta} />
      </main>
      <LandingFooter
        tagline={content.footer.tagline}
        legal={content.footer.legal}
        columns={[
          {
            heading: content.footer.platformLabel,
            links: [
              { label: 'inSuite', href: '/' },
              { label: content.ui.login, href: '/login' },
            ],
          },
          {
            heading: content.footer.sectionsLabel,
            links: content.sectionNav.items.map((item) => ({ label: item.label, href: `#${item.id}` })),
          },
        ]}
      />
    </div>
  );
}
